import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { subscriptionsApi } from '../../api/subscriptions'
import { useAuth } from '../../context/AuthContext'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import { FiCheck, FiInfo, FiBook } from 'react-icons/fi'
import './PackageSubjectSelection.css'

const PackageSubjectSelection = () => {
  const { packageId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [selectedSubjectIds, setSelectedSubjectIds] = useState(new Set())
  const [error, setError] = useState(null)

  const { data: packagesData, isLoading, error: queryError } = useQuery({
    queryKey: ['packages'],
    queryFn: () => subscriptionsApi.getPackages(),
    enabled: !!packageId,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 2
    },
  })

  const packageData = packagesData?.data?.packages?.find(pkg => pkg.id === parseInt(packageId)) || null
  const availableSubjects = packageData?.subjects || []

  const handleSubjectToggle = (subjectId) => {
    setSelectedSubjectIds(prev => {
      const newSet = new Set(prev)
      
      // Check max constraint
      if (newSet.has(subjectId)) {
        newSet.delete(subjectId)
      } else {
        if (packageData?.max_subjects && newSet.size >= packageData.max_subjects) {
          setError(`يرجى اختيار ما لا يزيد عن ${packageData.max_subjects} مادة`)
          setTimeout(() => setError(null), 3000)
          return prev
        }
        newSet.add(subjectId)
        setError(null)
      }
      
      return newSet
    })
  }

  const handleContinue = () => {
    // Validate min subjects
    if (packageData?.min_subjects && selectedSubjectIds.size < packageData.min_subjects) {
      setError(`يرجى اختيار ما لا يقل عن ${packageData.min_subjects} مادة`)
      return
    }

    // Validate max subjects
    if (packageData?.max_subjects && selectedSubjectIds.size > packageData.max_subjects) {
      setError(`يرجى اختيار ما لا يزيد عن ${packageData.max_subjects} مادة`)
      return
    }

    // Navigate to payment with selected subjects
    const subjectIdsArray = Array.from(selectedSubjectIds)
    navigate(`/payments?package_id=${packageId}&subject_ids=${subjectIdsArray.join(',')}`)
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  // Handle 401 errors gracefully - show login required instead of error
  if (queryError?.response?.status === 401) {
    return (
      <div className="package-subject-selection-page">
        <Header />
        <main className="package-subject-selection-main">
          <div className="container mx-auto px-4 py-8">
            {/* Login Required Banner */}
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى الباقات واختيار المواد</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
            
            {/* Show basic package info if available */}
            {packageData.name && (
              <div className="package-info-card">
                <div className="package-info-header">
                  <h1 className="package-name">{packageData.name}</h1>
                  {packageData.description && (
                    <p className="package-description">{packageData.description}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (!packageData) {
    return (
      <>
        <Header />
        <div className="error-container">
          <p>الباقة غير موجودة</p>
          <Button variant="primary" onClick={() => navigate('/subscriptions')}>
            العودة إلى الباقات
          </Button>
        </div>
        <Footer />
      </>
    )
  }

  return (
    <div className="package-subject-selection-page">
      <Header />
      <main className="package-subject-selection-main">
        <div className="container mx-auto px-4 py-8">
          {/* Login Required Banner */}
          {!isAuthenticated && (
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى الباقات واختيار المواد</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
          )}
          
          {/* Package Info Card */}
          <div className="package-info-card">
            <div className="package-info-header">
              <h1 className="package-name">{packageData.name}</h1>
              {packageData.description && (
                <p className="package-description">{packageData.description}</p>
              )}
            </div>
            <div className="package-constraints">
              {packageData.min_subjects && (
                <div className="constraint-badge">
                  الحد الأدنى: {packageData.min_subjects}
                </div>
              )}
              {packageData.max_subjects && (
                <div className="constraint-badge">
                  الحد الأقصى: {packageData.max_subjects}
                </div>
              )}
            </div>
          </div>

          {/* Selection Info */}
          <div className="selection-info-card">
            <FiInfo className="info-icon" />
            <span>
              {selectedSubjectIds.size} / {availableSubjects.length} مادة محددة
            </span>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {/* Subjects List */}
          <div className="subjects-section">
            <h2 className="section-title">اختر المواد</h2>
            {availableSubjects.length === 0 ? (
              <div className="empty-state">
                <p>لا توجد مواد متاحة لهذه الباقة</p>
              </div>
            ) : (
              <div className="subjects-list">
                {availableSubjects.map((subject) => {
                  const subjectId = typeof subject === 'object' ? subject.id : subject
                  const subjectName = typeof subject === 'object' ? subject.name : subject
                  const isSelected = selectedSubjectIds.has(subjectId)
                  
                  return (
                    <div
                      key={subjectId}
                      className={`subject-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSubjectToggle(subjectId)}
                    >
                      <div className="subject-checkbox">
                        {isSelected && <FiCheck className="check-icon" />}
                      </div>
                      <FiBook className="subject-icon" />
                      <span className="subject-name">{subjectName}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Continue Button */}
          <div className="continue-section">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleContinue}
              disabled={selectedSubjectIds.size === 0}
            >
              المتابعة إلى الدفع
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default PackageSubjectSelection
